/**
 * =============================================================================
 * IDS CRM Web - Distributor Products Component
 * =============================================================================
 *
 * PURPOSE:
 * Displays and manages product authorizations for a distributor.
 * Shows which products a distributor is authorized to sell and configures
 * payment milestones for each product.
 *
 * MILESTONES:
 * - On Order: Payment due when order is placed
 * - On Ship: Payment due when product ships
 * - On Delivery: Payment due on delivery
 * - On Install: Payment due after installation
 * - Monthly Service: Recurring monthly service payment
 *
 * Each milestone is split into: Customer Pays, IDS Split, Distributor Split
 *
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { resolveAssetImageSrc } from '../utils/assetTypes';
import { formatMajorMoney } from '../utils/money';
import './DistributorProducts.css';

const MILESTONE_LABELS = {
  on_order: 'On Order',
  on_ship: 'On Ship',
  on_delivery: 'On Delivery',
  on_install: 'On Install',
  monthly_service: 'Monthly Service'
};

const DEFAULT_MILESTONES = {
  on_order: { customer_pays: 0, ids_split: 0, distributor_split: 0 },
  on_ship: { customer_pays: 0, ids_split: 0, distributor_split: 0 },
  on_delivery: { customer_pays: 0, ids_split: 0, distributor_split: 0 },
  on_install: { customer_pays: 0, ids_split: 0, distributor_split: 0 },
  monthly_service: { customer_pays: 0, ids_split: 0, distributor_split: 0 }
};

function DistributorProducts({ distributorId, currency = 'GBP', assetTypes = [] }) {
  const { authFetch } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);

  // L10: shared Intl formatter replaces the old hardcoded symbol map
  const money = (amount) => formatMajorMoney(amount, currency);

  const toDisplayMilestones = (milestones = DEFAULT_MILESTONES) => {
    const display = {};
    Object.entries(DEFAULT_MILESTONES).forEach(([milestone, defaults]) => {
      display[milestone] = {};
      Object.keys(defaults).forEach(field => {
        display[milestone][field] = (milestones[milestone]?.[field] || 0) / 100;
      });
    });
    return display;
  };

  const toMinorUnitMilestones = (milestones = DEFAULT_MILESTONES) => {
    const minorUnits = {};
    Object.entries(DEFAULT_MILESTONES).forEach(([milestone, defaults]) => {
      minorUnits[milestone] = {};
      Object.keys(defaults).forEach(field => {
        minorUnits[milestone][field] = Math.round((Number(milestones[milestone]?.[field]) || 0) * 100);
      });
    });
    return minorUnits;
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await authFetch(`/api/v1/distributors/${distributorId}/products`);
        if (!res.ok) throw new Error('Failed to fetch products');
        const data = await res.json();

        const existingByType = {};
        for (const p of data.data || []) {
          existingByType[p.product_type] = p;
        }

        const merged = assetTypes.map(at => {
          const existing = existingByType[at.value];
          if (existing) {
            return {
              product_type: at.value,
              label: at.label,
              image: at.image,
              is_active: existing.is_active,
              payment_milestones: toDisplayMilestones(existing.payment_milestones || DEFAULT_MILESTONES),
              notes: existing.notes || '',
              effective_from: existing.effective_from ? existing.effective_from.split('T')[0] : '',
              effective_to: existing.effective_to ? existing.effective_to.split('T')[0] : ''
            };
          }
          return {
            product_type: at.value,
            label: at.label,
            image: at.image,
            is_active: false,
            payment_milestones: DEFAULT_MILESTONES,
            notes: '',
            effective_from: '',
            effective_to: ''
          };
        });

        setProducts(merged);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [distributorId, authFetch, assetTypes]);

  const toggleProduct = (productType) => {
    setProducts(prev => prev.map(p =>
      p.product_type === productType
        ? { ...p, is_active: !p.is_active }
        : p
    ));
  };

  const updateMilestone = (productType, milestone, field, value) => {
    const numVal = parseFloat(value) || 0;
    setProducts(prev => prev.map(p => {
      if (p.product_type !== productType) return p;
      return {
        ...p,
        payment_milestones: {
          ...p.payment_milestones,
          [milestone]: {
            ...p.payment_milestones[milestone],
            [field]: numVal
          }
        }
      };
    }));
  };

  const updateNotes = (productType, notes) => {
    setProducts(prev => prev.map(p =>
      p.product_type === productType ? { ...p, notes } : p
    ));
  };

  const updateEffectiveDate = (productType, field, value) => {
    setProducts(prev => prev.map(p =>
      p.product_type === productType ? { ...p, [field]: value } : p
    ));
  };

  const saveProducts = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const activeProducts = products.filter(p => p.is_active).map(p => ({
        product_type: p.product_type,
        is_active: true,
        payment_milestones: toMinorUnitMilestones(p.payment_milestones),
        notes: p.notes,
        effective_from: p.effective_from || null,
        effective_to: p.effective_to || null
      }));

      const res = await authFetch(`/api/v1/distributors/${distributorId}/products`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: activeProducts })
      });

      if (!res.ok) throw new Error('Failed to save products');

      setSuccess('Product authorizations saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getMilestoneTotal = (milestones, field) => {
    return Object.values(milestones).reduce((sum, m) => sum + (m[field] || 0), 0);
  };

  const getRowTotal = (milestoneValues) => {
    return (milestoneValues.customer_pays || 0) +
           (milestoneValues.ids_split || 0) +
           (milestoneValues.distributor_split || 0);
  };

  if (loading) {
    return <div className="distributor-products loading">Loading product authorizations...</div>;
  }

  const activeCount = products.filter(p => p.is_active).length;

  return (
    <div className="distributor-products">
      <div className="products-header">
        <h3>Authorised Products</h3>
        <p className="products-description">
          Select which products this distributor is authorised to sell and configure the payment milestones for each.
        </p>
      </div>

      {error && <div className="products-error">{error}</div>}
      {success && <div className="products-success">{success}</div>}

      <div className="products-grid">
        {products.map(product => (
          <div
            key={product.product_type}
            className={`product-card ${product.is_active ? 'active' : ''} ${expandedProduct === product.product_type ? 'expanded' : ''}`}
          >
            <div className="product-header" onClick={() => toggleProduct(product.product_type)}>
              <div className="product-toggle">
                <input
                  type="checkbox"
                  checked={product.is_active}
                  onChange={() => toggleProduct(product.product_type)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <img
                src={resolveAssetImageSrc(product.image)}
                alt={product.label}
                className="product-image"
                onError={(e) => { e.target.src = '/asset-types/asset-default.svg'; }}
              />
              <div className="product-info">
                <span className="product-name">{product.label}</span>
                {product.is_active && (
                  <span className="product-status">Authorised</span>
                )}
              </div>
              {product.is_active && (
                <button
                  className="expand-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedProduct(expandedProduct === product.product_type ? null : product.product_type);
                  }}
                >
                  {expandedProduct === product.product_type ? '▲' : '▼'}
                </button>
              )}
            </div>

            {product.is_active && expandedProduct === product.product_type && (
              <div className="product-milestones">
                <h4>Payment Milestones ({currency})</h4>
                <table className="milestones-table">
                  <thead>
                    <tr>
                      <th>Milestone</th>
                      <th>Customer Pays</th>
                      <th>IDS Split</th>
                      <th>Distributor Split</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(MILESTONE_LABELS).map(([key, label]) => {
                      const values = product.payment_milestones[key] || DEFAULT_MILESTONES[key];
                      return (
                        <tr key={key}>
                          <td className="milestone-label">{label}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={values.customer_pays || ''}
                              onChange={(e) => updateMilestone(product.product_type, key, 'customer_pays', e.target.value)}
                              placeholder="0.00"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={values.ids_split || ''}
                              onChange={(e) => updateMilestone(product.product_type, key, 'ids_split', e.target.value)}
                              placeholder="0.00"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={values.distributor_split || ''}
                              onChange={(e) => updateMilestone(product.product_type, key, 'distributor_split', e.target.value)}
                              placeholder="0.00"
                            />
                          </td>
                          <td className="row-total">
                            {money(getRowTotal(values))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="totals-row">
                      <td><strong>Totals</strong></td>
                      <td><strong>{money(getMilestoneTotal(product.payment_milestones, 'customer_pays'))}</strong></td>
                      <td><strong>{money(getMilestoneTotal(product.payment_milestones, 'ids_split'))}</strong></td>
                      <td><strong>{money(getMilestoneTotal(product.payment_milestones, 'distributor_split'))}</strong></td>
                      <td className="row-total">
                        <strong>
                          {money(
                            getMilestoneTotal(product.payment_milestones, 'customer_pays') +
                            getMilestoneTotal(product.payment_milestones, 'ids_split') +
                            getMilestoneTotal(product.payment_milestones, 'distributor_split')
                          )}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>

                <div className="milestone-notes">
                  <div className="effective-dates">
                    <label>
                      Effective from
                      <input
                        type="date"
                        value={product.effective_from || ''}
                        onChange={(e) => updateEffectiveDate(product.product_type, 'effective_from', e.target.value)}
                      />
                    </label>
                    <label>
                      Effective to
                      <input
                        type="date"
                        value={product.effective_to || ''}
                        onChange={(e) => updateEffectiveDate(product.product_type, 'effective_to', e.target.value)}
                      />
                    </label>
                  </div>
                  <label>Notes</label>
                  <textarea
                    value={product.notes || ''}
                    onChange={(e) => updateNotes(product.product_type, e.target.value)}
                    placeholder="Any notes about this product agreement..."
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="products-footer">
        <span className="active-count">{activeCount} of {products.length} products authorised</span>
        <button
          className="save-btn"
          onClick={saveProducts}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Product Authorizations'}
        </button>
      </div>
    </div>
  );
}

export default DistributorProducts;
